'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { type CSSProperties, useState } from 'react'

import type { ModelsNews } from '@/api/generated-api'
import { isHexColor, readableTextColor } from '@/lib/color/contrast'
import { useHydratedAuthUi } from '@/lib/react/useHydratedAuthUi'
import { useAuth } from '@/providers/AuthProvider'

import S from './NewsArticleCta.module.scss'

const NEWS_ARTICLE_CTA_IMAGE = '/assets/news-article-cta-background.png'

const trimmed = (value?: string) => value?.trim() || undefined

type NewsArticleCtaProps = {
  news?: ModelsNews
}

const NewsArticleCta = ({ news }: NewsArticleCtaProps) => {
  const t = useTranslations('news.detail.cta')
  const { openAuthModal, isAuthenticated } = useAuth()
  const hasHydratedAuthUi = useHydratedAuthUi()
  // Legacy rows hold a plain link in button_url instead of an uploaded banner,
  // so a broken image falls back to the default artwork rather than a blank box.
  const [bannerFailed, setBannerFailed] = useState(false)

  const banner = bannerFailed ? undefined : trimmed(news?.button_url)
  const label = trimmed(news?.button_text)
  const href = trimmed(news?.redirect_url)

  // An uploaded banner is assumed to carry its own copy, so the default
  // headline is dropped unless the article supplies one of its own.
  const title = trimmed(news?.banner_title) ?? (banner ? undefined : t('title'))
  const subtitle =
    trimmed(news?.banner_subtitle) ?? (banner ? undefined : t('subtitle'))

  const isDefaultSignupCta = !href && !label

  // An invalid hex keeps the site accent rather than painting the button with
  // something the admin never previewed. 88% against black reproduces the
  // accent/accent-hover relationship the tokens already use.
  const accent = trimmed(news?.button_color)
  const buttonStyle =
    accent && isHexColor(accent)
      ? ({
          '--cta-button-bg': accent,
          '--cta-button-bg-hover': `color-mix(in srgb, ${accent} 88%, #000)`,
          '--cta-button-fg': readableTextColor(accent)
        } as CSSProperties)
      : undefined

  if (isDefaultSignupCta && hasHydratedAuthUi && isAuthenticated) {
    return null
  }

  return (
    <section className={S.cta} aria-label={t('ariaLabel')}>
      <div className={S.ctaInner}>
        <Image
          key={banner ?? NEWS_ARTICLE_CTA_IMAGE}
          src={banner ?? NEWS_ARTICLE_CTA_IMAGE}
          alt=""
          fill
          sizes="100vw"
          // The uploaded banner goes through /_next/image so it is served from
          // our own origin: img-src only allows the CDN host, and the admin can
          // paste a URL from anywhere.
          unoptimized={!banner}
          className={S.ctaImage}
          onError={() => setBannerFailed(true)}
        />
        <div className={S.ctaContent}>
          {title || subtitle ? (
            <div className={S.ctaCopy}>
              {title ? <h2 className={S.ctaTitle}>{title}</h2> : null}
              {subtitle ? <p className={S.ctaSubtitle}>{subtitle}</p> : null}
            </div>
          ) : null}
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={S.ctaButton}
              style={buttonStyle}
            >
              {label ?? t('button')}
            </a>
          ) : (
            <button
              type="button"
              className={S.ctaButton}
              style={buttonStyle}
              onClick={() => openAuthModal('emailSignupEntityChoose')}
            >
              {label ?? t('button')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export default NewsArticleCta
